<template>
	<div class="wrapper">
		<div v-if="loading" class="text-center">
			<b-spinner label="Loading..." style="width: 3rem; height: 3rem;" variant="info"></b-spinner>
		</div>
		<div v-else class="animated fadeIn">
			<b-row>
				<b-col cols="12">
					<b-card header-tag="header">
						<template v-slot:header>
							<i class="icon-pencil"></i> Create New Delivery Receipt
						</template>
						<b-card-body>
							<b-form @submit.prevent="checkFormValidity()" method="post">
								<b-form-group
									label="Select Purchase Order"
									label-for="purchaseOrder"
									label-class="required"
									:label-cols="3"
									:horizontal="true"
									invalid-feedback="Please select one."
									:state="$v.form.purchaseOrderId.$dirty ? !$v.form.purchaseOrderId.$error : null">
									<model-select id="purchaseOrder" :options="purchaseOrderDropDownList" v-model="form.purchaseOrderId" :isError="$v.form.purchaseOrderId.$dirty ? $v.form.purchaseOrderId.$error : false" placeholder="Select Purchase Order"></model-select>
								</b-form-group>
								<div v-if="form.purchaseOrderId != null">
									<b-form-group
										label="Date of Delivery" label-for="deliveryDate"
										label-class="required"
										:label-cols="3"
										:horizontal="true">
										<b-form-input v-model="form.deliveryDate" name="deliveryDate" type="date" id="deliveryDate" :state="$v.form.deliveryDate.$dirty ? !$v.form.deliveryDate.$error : null" aria-describedby="delivery-date-live-feedback"></b-form-input>
										<b-form-invalid-feedback id="delivery-date-live-feedback">This is a required field.</b-form-invalid-feedback>
									</b-form-group>
									<b-form-group
										label="Challan No." label-for="receipt-no"
										label-class="required"
										:label-cols="3"
										:horizontal="true">
										<b-form-input v-model="form.receiptNo" name="receiptNo" type="text" id="receipt-no" :state="$v.form.receiptNo.$dirty ? !$v.form.receiptNo.$error : null" aria-describedby="receipt-number-live-feedback"></b-form-input>
										<b-form-invalid-feedback id="receipt-number-live-feedback">This is a required field.</b-form-invalid-feedback>
									</b-form-group>
									<b-form-group
										label="Number of Devices "
										:label-cols="3"
										:horizontal="true">
										<b-card-text><strong>{{purchaseOrder.deviceQuantity}}</strong></b-card-text>
									</b-form-group>
									<b-form-group
										label="Select Device Identifiers" label-for="identifier-list"
										label-class="required"
										:label-cols="3"
										:horizontal="true"
										invalid-feedback="Number of selected devices does not match the ordered quantity."
										:state="$v.form.selectedIdentifiers.$dirty ? !$v.form.selectedIdentifiers.$error : null">
										<multi-select
											:options="deviceIdentifierDropDownList"
											:selected-options="form.selectedIdentifiers"
											@select="onSelectDeviceIdentifier"
											:isError="$v.form.selectedIdentifiers.$dirty ? $v.form.selectedIdentifiers.$error : false"
											placeholder="Available Devices"></multi-select>
									</b-form-group>
								</div>

								<div class="form-actions">
									<b-button type="submit" variant="primary"><i class="fa fa-dot-circle-o"></i> Create Delivery Receipt</b-button>
									&nbsp;
									<b-button type="reset" variant="danger" @click="resetForm()"><i class="fa fa-ban"></i> Reset</b-button>
								</div>
							</b-form>
						</b-card-body>
					</b-card>
				</b-col>
			</b-row>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import { required, numeric, minValue, requiredIf, minLength, maxLength } from "vuelidate/lib/validators";
import toastService from "@/services/toastService";
import purchaseOrderService from "@/services/purchaseOrderService";
import deliveryDetailsService from "@/services/deliveryDetailsService";
import {ModelSelect, MultiSelect} from "vue-search-select";
import resellerService from "@/services/resellerService";
import customerService from "@/services/customerService";

export default {
	name : "CreateDeliveryReceipt",
	components : {
		ModelSelect,
		MultiSelect
	},
	data : function () {
		return {
			purchaseOrderList              : [],
			purchaseOrderDropDownList      : [],
			purchaseOrder                  : null,
			resellerStock                  : null,
			deviceIdentifierDropDownList   : [],
			deviceIdentifierSelectionLimit : 0,
			form                           : {
				purchaseOrderId : null,
				deliveryDate : null,
				receiptNo : null,
				selectedIdentifiers : []
			},
			loading                        : false
		}
	},
	validations : function() {
		return {
			form : {
				purchaseOrderId : {
					required,
					minValue: minValue(1)
				},
				deliveryDate : {
					required
				},
				receiptNo : {
					required
				},
				selectedIdentifiers : {
					required,
					minLength : minLength(this.deviceIdentifierSelectionLimit),
					maxLength : maxLength(this.deviceIdentifierSelectionLimit),
					$each : {
						value : {
							required
						}
					}
				}
			}
		}
	},
	methods : {
		checkFormValidity : function (){
			let formHasErrors = false;

			this.$v.form.$touch();
			if (this.$v.form.$anyError) {
				formHasErrors = true;
			}

			if(formHasErrors) return;
			else this.createDeliveryReceipt();
		},
		createDeliveryReceipt : function () {
			const formData = {
				purchaseOrderId : this.form.purchaseOrderId,
				deliveryDate : new moment(this.form.deliveryDate,'YYYY-MM-DD').format('x'),
				deliveryReceiptNo : this.form.receiptNo.trim(),
				deviceIdentifiers : this.form.selectedIdentifiers.map(item => item.value.trim())
			}

			deliveryDetailsService.createDeliveryReceipt(formData, (data) => {
				this.resetForm();
				this.purchaseOrderList = this.purchaseOrderList.filter((item) => item.id !== formData.purchaseOrderId);
				this.purchaseOrderDropDownList = this.purchaseOrderDropDownList.filter((item) => item.value !== formData.purchaseOrderId);
				toastService.showSuccessToast("Delivery receipt created successfully");
			}, (error) => {
				this.$nextTick(() => { this.$v.$reset() });
				toastService.showErrorToast("Could not create purchase order " + error);
			});
		},
		resetForm: function () {
			this.$nextTick(() => { this.$v.$reset() });
			this.form.purchaseOrderId = null;
			this.form.deliveryDate = null;
			this.form.receiptNo = null;
			this.form.selectedIdentifiers = [];
		},
		getAllUndeliveredPurchaseOrders : function () {
			this.loading = true;
			purchaseOrderService.getAllUndeliveredPurchaseOrders((data) => {
				this.purchaseOrderList = data;
				this.createPurchaseOrderDropDownList();
				this.loading = false;
			}, (error) => {
				this.loading = false
				toastService.showErrorToast("Could not fetch purchase order list " + error);
			});
		},
		createPurchaseOrderDropDownList : function () {
			this.purchaseOrderList.forEach((value) => {
				let purchaseOrderOption = new Object();
				purchaseOrderOption.text = value.id + " - " + value.projectName + " - " + moment(value.purchaseDate).format('DD/MM/YYYY');
				purchaseOrderOption.value = value.id;
				this.purchaseOrderDropDownList.push(purchaseOrderOption);
			});
		},
		getPurchaseOrderDetails : function (purchaseOrderId) {
			this.purchaseOrder = this.purchaseOrderList.find((item) => item.id === purchaseOrderId);
			this.deviceIdentifierSelectionLimit = this.purchaseOrder.deviceQuantity;
			this.resellerStock = null;
			this.getResellerStock(this.purchaseOrder.projectId);
			this.initForm();
		},
		initForm : function () {
			this.$nextTick(() => { this.$v.$reset() });
			this.form.deliveryDate = null;
			this.form.receiptNo = null;
			this.form.selectedIdentifiers = [];
		},
		getResellerStock : function (projectId) {
			this.loading = true;
			resellerService.getResellerStockByProjectId(projectId, (data) => {
				this.resellerStock = data;
				this.createDeviceIdentifierDropDownList(this.resellerStock);
				this.loading = false;
				this.getProjectDeviceList(projectId);
			}, (error) => {
				this.loading = false
				toastService.showErrorToast("Could not fetch reseller stock " + error);
			});
		},
		createDeviceIdentifierDropDownList : function (data) {
			this.deviceIdentifierDropDownList = [];
			data.forEach((value) => {
				let deviceIdentifierOption = new Object();
				deviceIdentifierOption.text = value.identifier;
				deviceIdentifierOption.value = value.identifier;
				this.deviceIdentifierDropDownList.push(deviceIdentifierOption);
			});
		},
		onSelectDeviceIdentifier : function (items) {
			this.form.selectedIdentifiers = items;
		},
		getProjectDeviceList : function (projectId){
			this.loading = true;
			customerService.getProjectDeviceList(projectId, (data) => {
				if(Array.isArray(data) && data.length > 0)
					this.appendToDeviceIdentifierDropDownList(data);
				this.loading = false;
			}, (error) => {
				this.loading = false
				toastService.showErrorToast("Could not fetch device list of this project " + error);
			});
		},
		appendToDeviceIdentifierDropDownList : function (data) {
			data.forEach((value) => {
				let deviceIdentifierOption = new Object();
				deviceIdentifierOption.text = value.identifier + " **";
				deviceIdentifierOption.value = value.identifier;
				this.deviceIdentifierDropDownList.push(deviceIdentifierOption);
			});
		}
	},
	created() {
		this.getAllUndeliveredPurchaseOrders();
	},
	computed : {
		purchaseOrderId : function () {
			return this.form.purchaseOrderId;
		}
	},
	watch : {
		purchaseOrderId : function (newVal, oldVal){
			if(newVal != null && newVal !== oldVal){
				this.getPurchaseOrderDetails(newVal);
			}
		}
	}
}
</script>

<style src="vue-search-select/dist/VueSearchSelect.css"></style>
